import React, {useEffect, useState} from 'react';
import {Trans} from '@app/i18n/trans';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {CloseIcon} from '@app/ui/icons/material/Close';
import {loadStripe} from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import {
  fetchStatus,
  getClientSecret,
} from '../files/queries/use-paginated-entries';

const stripe_published_key = import.meta.env.VITE_API_STRIPE_KEY;
const stripePromise = loadStripe(stripe_published_key);

interface AddFundsProps {
  setOpen: (open: boolean) => void;
  sessionId: any;
}

export function AddFunds({setOpen, sessionId}: AddFundsProps) {
  const [paymentAmount, setPaymentAmount] = useState('');
  const [error, setError] = useState({paymentAmountError: ''});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const amountOptions = [25, 50, 150, 250];
  const mutation = getClientSecret();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPaymentAmount(value);
    if (value && !amountOptions.includes(Number(value))) {
      setError(prev => ({
        ...prev,
        paymentAmountError: 'Please select a valid amount from the dropdown',
      }));
    } else {
      setError(prev => ({...prev, paymentAmountError: ''}));
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectAmount = (amount: number) => {
    setPaymentAmount(amount.toString());
    setError(prev => ({...prev, paymentAmountError: ''}));
    handleCloseMenu();
  };

  const onClose = () => setOpen(false);

  useEffect(() => {
    if (!paymentAmount) return;
    setIsLoading(true);
    mutation.mutate(
      {
        amount: Number(paymentAmount),
        return_url: window.location.href,
      },
      {
        onSuccess: (data: any) => {
          setIsLoading(false);
          if (data?.clientSecret) {
            setClientSecret(data.clientSecret);
          }
        },
        onError: () => {
          setIsLoading(false);
          setClientSecret(null);
        },
      }
    );
  }, [paymentAmount]);

  useEffect(() => {
    if (sessionId) {
      setIsLoading(true);
      console.log('Processing payment with session ID:', sessionId);
      
      fetchStatus(sessionId)
        .then(data => {
          console.log('Payment status response:', data);
          setIsLoading(false);
          
          if (data.payment_status === 'paid') {
            console.log('Payment successful! Amount:', data.amount_total);
            setIsPaymentSuccess(true);
            
            // Show success message for 3 seconds before closing
            setTimeout(() => {
              setOpen(false);
              const url = window.location.origin + window.location.pathname;
              window.history.replaceState({}, document.title, url);
              // Reload to refresh the balance and transactions
              window.location.reload();
            }, 3000);
          } else {
            console.warn('Payment not completed. Status:', data.payment_status);
            // Remove session_id from URL and reset
            const url = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, url);
            setPaymentAmount('25');
          }
        })
        .catch(err => {
          console.error('Error fetching payment status:', err);
          setIsLoading(false);
          
          // Remove session_id from URL and reset
          const url = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, url);
          setPaymentAmount('25');
        });
    } else {
      setPaymentAmount('25');
    }
  }, [sessionId]);

  return (
    <Dialog open={true} onClose={onClose} maxWidth={isPaymentSuccess ? "md" : "md"} fullWidth>
      {isPaymentSuccess ? (
        <>
          <DialogTitle className="flex items-center justify-between">
            <p className="text-xl font-medium">
              <Trans message="Payment Successfull" />
            </p>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-10">
              {isLoading ? (
                <div className="mt-6 flex justify-center">
                  <CircularProgress />
                </div>
              ) : (
                <>Payment Success!</>
              )}
            </div>
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle className="flex items-center justify-between">
            <p className="text-xl font-medium">
              <Trans message="Pay Now" />
            </p>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <hr />
          <DialogContent>
            <div className="flex flex-col gap-10">
              {isLoading ? (
                <div className="mt-6 flex justify-center">
                  <CircularProgress />
                </div>
              ) : clientSecret ? (
                <>
                  {!isPaymentSuccess && (
                    <>
                      {/* Payment Method */}
                      <div className="mb-6 flex w-full items-center gap-10">
                        <p className="w-1/3 text-xl font-medium">
                          <Trans message="Payment Method:" />
                        </p>
                        <div className="ml-20 flex gap-10 p-4">
                          <div
                            className={`cursor-pointer rounded-lg border-4 border-[#0000001f] p-2 shadow-md transition`}
                          >
                            <div className="flex h-[120px] w-[120px] items-center justify-center bg-[#635BFF] text-white text-2xl font-bold rounded">
                              STRIPE
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Payment Amount */}
                      <div className="mb-6 flex w-full items-center">
                        <p className="w-1/3 text-xl font-medium">
                          <Trans message="Payment Amount:" />
                        </p>
                        <div className="relative w-2/3">
                          <span
                            className={`absolute left-40 ${
                              error?.paymentAmountError ? 'top-1/3' : 'top-1/2'
                            } -translate-y-1/2 transform rounded border bg-[#93919166] px-10 py-4 text-lg font-semibold text-black`}
                          >
                            GBP £
                          </span>
                          <div className="paymentAmount">
                            <TextField
                              fullWidth
                              className="myCustomTextFieldClass"
                              variant="outlined"
                              value={paymentAmount}
                              InputProps={{
                                endAdornment: (
                                  <span
                                    className="text-gray-500 cursor-pointer text-2xl"
                                    onClick={handleOpenMenu}
                                  >
                                    ▼
                                  </span>
                                ),
                              }}
                            />
                            <Menu
                              className="myCustomMenuClass"
                              anchorEl={anchorEl}
                              open={Boolean(anchorEl)}
                              onClose={handleCloseMenu}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                              }}
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                              }}
                            >
                              {amountOptions.map(amount => (
                                <MenuItem
                                  className="myCustomMenuItemClass"
                                  key={amount}
                                  onClick={() => handleSelectAmount(amount)}
                                >
                                  £{amount}
                                </MenuItem>
                              ))}
                            </Menu>
                          </div>
                          {error && error.paymentAmountError && (
                            <p className="mt-2 pl-28 text-sm text-[#f44336]">
                              {error.paymentAmountError}
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="mt-6">
                    <EmbeddedCheckoutProvider
                      key={clientSecret}
                      stripe={stripePromise}
                      options={{clientSecret}}
                    >
                      <EmbeddedCheckout />
                    </EmbeddedCheckoutProvider>
                  </div>
                </>
              ) : null}
            </div>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
